import { useCallback, useRef } from 'react';

import { Alert } from '@/utils/alert';

/**
 * Wrap a close/cancel handler so it prompts before discarding unsaved
 * changes. The caller flips `markDirty()` on first edit; consumers should
 * call `markClean()` after a successful save before closing.
 *
 * Usage:
 *   const { markDirty, markClean, confirmClose } = useDirtyConfirm();
 *   ...
 *   <TextInput onChangeText={(v) => { markDirty(); setX(v); }} />
 *   <Pressable onPress={() => confirmClose(onClose)} />
 *
 * Why a hook over a HOC: forms in this codebase are mostly React function
 * components with co-located state, so the hook composes cleanly without
 * having to lift state into a wrapper.
 */
export function useDirtyConfirm(): {
	markDirty: () => void;
	markClean: () => void;
	isDirty: () => boolean;
	confirmClose: (close: () => void) => void;
	} {
	const dirty = useRef(false);
	const markDirty = useCallback(() => { dirty.current = true; }, []);
	const markClean = useCallback(() => { dirty.current = false; }, []);
	const isDirty = useCallback(() => dirty.current, []);
	const confirmClose = useCallback((close: () => void) => {
		if (!dirty.current) { close(); return; }
		Alert.alert(
			'Discard changes?',
			'You have unsaved edits. Closing will discard them.',
			[
				{ text: 'Keep editing', style: 'cancel' },
				{ text: 'Discard', style: 'destructive', onPress: () => { dirty.current = false; close(); } },
			],
		);
	}, []);
	return { markDirty, markClean, isDirty, confirmClose };
}
